import type { FakeStore, OwnedMembership, UserFakeDoc } from './store/types'

interface FoundMembership {
  pid: string;
  doc: UserFakeDoc;
  membership: OwnedMembership;
}

function jsonResponse(body: unknown, init: ResponseInit = {}): Response {
  return new Response(JSON.stringify(body), {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  })
}

function unauthorized(): Response {
  return jsonResponse({ errors: 'Unauthorized' }, { status: 401 })
}

function hasBasicAuth(request: Request): boolean {
  const header = request.headers.get('authorization') ?? ''
  return header.startsWith('Basic ')
}

async function findByMvaultId(store: FakeStore, mvaultId: string): Promise<FoundMembership | null> {
  const pid = await store.findPidByMvaultId(mvaultId)
  if(!pid) return null

  const doc = await store.getUserDoc(pid)
  if(!doc || !doc.enabled) return null

  const membership = doc.memberships.find(m => m.data.mvault_id === mvaultId)
  if(!membership) return null

  return { pid, doc, membership }
}

async function cancelRecurringPayment(request: Request, mvaultId: string, store: FakeStore): Promise<Response | null> {
  const found = await findByMvaultId(store, mvaultId)
  if(!found) return null

  if(!hasBasicAuth(request)) return unauthorized()

  const { pid, doc, membership } = found

  membership.data.membership_status = 'expired'
  membership.data.self_cancel = false
  if(membership.data.donation_info && 'next_billing_cycle' in membership.data.donation_info) {
    membership.data.donation_info.next_billing_cycle = null
  }

  await store.putUserDoc(pid, doc)
  return jsonResponse({}, { status: 200 })
}

// GET /memberships/membership_settings/?pid=...&email=...&sort_station_id=...&filter_station_id=...&status=...
async function membershipSettings(request: Request, url: URL, store: FakeStore): Promise<Response | null> {
  const pid = url.searchParams.get('pid')

  // The store is keyed by pid, and getMembershipSettings always sends one.
  // Email-only lookups can't be answered from fake state, so they belong to
  // the real upstream (each row still carries `email` for fidelity).
  if(!pid) return null

  // No doc, or fakes explicitly switched off for this user → this request
  // belongs to the real upstream. Returning null falls through.
  const doc = await store.getUserDoc(pid)
  if(!doc || !doc.enabled) return null

  if(!hasBasicAuth(request)) return unauthorized()

  // .org sends sort_station_id (matching cards first); SVP sends filter_station_id (matching cards only).
  const sortStationId = url.searchParams.get('sort_station_id')
  const filterStationId = url.searchParams.get('filter_station_id')
  const status = url.searchParams.get('status')

  const allOwned = doc.memberships

  // 204: no records exist for this user at all.
  if(allOwned.length === 0) {
    return new Response(null, { status: 204 })
  }

  let owned = allOwned.filter(m => m.displayable !== false)
  if(status) {
    owned = owned.filter(m => m.data.membership_status === status)
  }

  // SVP: filter down to only the localized station's memberships.
  if(filterStationId) {
    owned = owned.filter(m => m.data.station_info.station_id === filterStationId)
  }

  // 404: records exist but none are displayable (status=off or past grace).
  // Matches real MVault behavior verified against staging.
  if(owned.length === 0) {
    return jsonResponse({ errors: 'No memberships found.' }, { status: 404 })
  }

  // .org: sort stationId-matching memberships first (mirrors MVault's first_station annotation)
  if(sortStationId) {
    owned = [...owned].sort((a, b) => {
      const aMatch = a.data.station_info.station_id === sortStationId ? 0 : 1
      const bMatch = b.data.station_info.station_id === sortStationId ? 0 : 1
      return aMatch - bMatch
    })
  }

  return jsonResponse({
    collection_info: {
      current_page_number: 1,
      total_items_count: owned.length,
      items_per_page: 50,
      items_current_page: owned.length,
    },
    objects: owned.map(o => o.data),
  })
}

// Public dispatcher. Returns null when the request doesn't match a fake MVault
// route or the fake state doesn't claim it. null means "not faked, use the
// real upstream".
export function handle(request: Request, url: URL, pathBelowMount: string, store: FakeStore): Promise<Response | null> | null {
  if(request.method === 'GET' && pathBelowMount === '/memberships/membership_settings/') {
    return membershipSettings(request, url, store)
  }

  const cancelMatch = pathBelowMount.match(/^\/memberships\/by_mvault_id\/([^/]+)\/cancel_recurring_payment\/?$/)
  if(cancelMatch && request.method === 'POST') {
    const [, mvaultId] = cancelMatch
    return cancelRecurringPayment(request, mvaultId, store)
  }

  return null
}
